import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAdminApi } from '@/services/AdminApi';
import { type ILogger } from '@/services/Logger';
import { type AdminClient, type ClientSpec } from '@/domain/admin';

/**
 * The client-management ViewModel: lists clients and drives create / update / rotate-secret / delete. A
 * generated secret (create of a confidential client, or rotate) is surfaced via `revealedSecret` for ONE-TIME
 * display: the store holds it transiently so the view can show it once, then it is cleared. Verified against
 * `FakeAdminApi`.
 */
export class ClientsStore {
  private _clients: AdminClient[] = [];
  private _selected: string | null = null;
  private _loading = false;
  private _busy = false;
  private _error: string | null = null;
  private _notice: string | null = null;
  private _revealedSecret: { clientId: string; secret: string } | null = null;

  constructor(
    private api: IAdminApi,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get clients(): readonly AdminClient[] {
    return this._clients;
  }
  get loading(): boolean {
    return this._loading;
  }
  get busy(): boolean {
    return this._busy;
  }
  get error(): string | null {
    return this._error;
  }
  get notice(): string | null {
    return this._notice;
  }
  get selectedClientId(): string | null {
    return this._selected;
  }
  get selected(): AdminClient | null {
    return this._clients.find((c) => c.client_id === this._selected) ?? null;
  }
  /** A just-generated secret to show ONCE (create/rotate). Null after `dismissSecret`. */
  get revealedSecret(): { clientId: string; secret: string } | null {
    return this._revealedSecret;
  }

  async load(): Promise<void> {
    this._loading = true;
    this._error = null;
    try {
      const clients = await this.api.listClients();
      runInAction(() => {
        this._clients = clients.slice().sort((a, b) => a.client_id.localeCompare(b.client_id));
      });
    } catch (e) {
      this.fail('load clients', e);
    } finally {
      runInAction(() => (this._loading = false));
    }
  }

  select(clientId: string | null): void {
    this._selected = clientId;
    this._notice = null;
    this._error = null;
  }

  dismissSecret(): void {
    this._revealedSecret = null;
  }

  async create(clientId: string, spec: ClientSpec, isPublic: boolean): Promise<void> {
    const id = clientId.trim();
    if (!id) {
      this._error = 'a client id is required';
      return;
    }
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      const created = await this.api.createClient(id, spec, isPublic);
      await this.load();
      runInAction(() => {
        this._notice = `created client "${id}"`;
        this._selected = id;
        if (created.secret) this._revealedSecret = { clientId: id, secret: created.secret };
      });
    } catch (e) {
      this.fail('create client', e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  async update(spec: ClientSpec): Promise<void> {
    const id = this._selected;
    if (!id) return;
    await this.run('updated client', () => this.api.updateClient(id, spec));
  }

  async rotateSecret(): Promise<void> {
    const id = this._selected;
    if (!id) return;
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      const secret = await this.api.rotateSecret(id);
      runInAction(() => {
        this._notice = `rotated secret for "${id}"`;
        this._revealedSecret = { clientId: id, secret };
      });
    } catch (e) {
      this.fail('rotate secret', e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  async remove(): Promise<void> {
    const id = this._selected;
    if (!id) return;
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      await this.api.deleteClient(id);
      runInAction(() => {
        this._selected = null;
        this._notice = `deleted client "${id}"`;
      });
      await this.load();
    } catch (e) {
      this.fail('delete client', e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  private async run(label: string, fn: () => Promise<void>): Promise<void> {
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      await fn();
      runInAction(() => (this._notice = label));
      await this.load();
    } catch (e) {
      this.fail(label, e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  private fail(what: string, e: unknown): void {
    this.logger.error(`${what} failed`, e);
    runInAction(() => (this._error = `${what} failed: ${e instanceof Error ? e.message : String(e)}`));
  }
}

export const TOKEN_ClientsStore = token<ClientsStore>('ClientsStore');
